import React, { Suspense, lazy } from 'react';
import { Hero } from './components/Hero';

/**
 * WOODWORKING FURNITURE PLANS™ sales page.
 * HERO → SNAPSHOT → P04 PREVIEW → PLAN → TECH PROOF → 6 VOLUMES → 5 BONUSES →
 * OFFER → FAQ → FINAL CTA → FOOTER (+ mobile sticky bar after #offer).
 *
 * Everything below the hero loads in separate chunks so the hero (mobile LCP)
 * ships with minimal JS. Each block reserves height while loading.
 */
const named = <K extends string,>(loader: () => Promise<Record<K, React.ComponentType>>, key: K) => lazy(() => loader().then(m => ({
  default: m[key]
})));
const ValueStrip = named(() => import('./components/sections/ValueStrip'), 'ValueStrip');
const ProjectPreviewSection = named(() => import('./components/sections/ProjectPreviewSection'), 'ProjectPreviewSection');
const PlanSection = named(() => import('./components/sections/PlanSection'), 'PlanSection');
const TechProofSection = named(() => import('./components/sections/TechProofSection'), 'TechProofSection');
const CollectionSection = named(() => import('./components/sections/CollectionSection'), 'CollectionSection');
const BonusSection = named(() => import('./components/sections/BonusSection'), 'BonusSection');
const OfferSection = named(() => import('./components/sections/OfferSection'), 'OfferSection');
const FaqSection = named(() => import('./components/sections/FaqSection'), 'FaqSection');
const FinalCta = named(() => import('./components/sections/FinalCta'), 'FinalCta');
const Footer = named(() => import('./components/Footer'), 'Footer');
const StickyBuyBar = named(() => import('./components/StickyBuyBar'), 'StickyBuyBar');
interface DeferredProps {
  children: React.ReactNode;
  minHeight: string;
  background?: string;
  id?: string;
}

/** The fallback carries the section id so anchors resolve even before the chunk loads. */
function Deferred({
  children,
  minHeight,
  background = 'bg-charcoal',
  id
}: DeferredProps) {
  return <Suspense fallback={<div id={id} className={`w-full ${background}`} style={{
    minHeight
  }} aria-hidden data-id="element-1" data-mp="sssrer5goibg" />} data-id="element-0" data-mp="stc7e2bjwwmu">
      {children}
    </Suspense>;
}
export function App() {
  return <main className="w-full overflow-x-clip bg-charcoal text-white" data-id="element-2" data-mp="3ubrfeek9e61c">
      <Hero data-id="element-3" data-mp="d4e6dvbvvgxr" />

      <Deferred minHeight="64px" background="bg-bottle" data-id="element-4" data-mp="d57fh2ybeij2">
        <ValueStrip data-id="element-5" data-mp="d2l7j27ii10b" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-cream" id="project-preview" data-id="element-6" data-mp="851m9icecnyv">
        <ProjectPreviewSection data-id="element-7" data-mp="3gle90gn0mze4" />
      </Deferred>
      <Deferred minHeight="420px" background="bg-dune" data-id="element-8" data-mp="3gm66jx06b771">
        <PlanSection data-id="element-9" data-mp="3bmjq7ep1tau0" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-cream" id="technical-proof" data-id="element-10" data-mp="3bnmfxjamx78c">
        <TechProofSection data-id="element-11" data-mp="3qbfwb67jodrz" />
      </Deferred>
      <Deferred minHeight="1000px" background="bg-dune" id="volumes" data-id="element-12" data-mp="3qcipjkscasb2">
        <CollectionSection data-id="element-13" data-mp="3ljphoo1kr3ur" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-cream" id="bonuses" data-id="element-14" data-mp="3limr6h85z3fd">
        <BonusSection data-id="element-15" data-mp="2xtbw9gdhw395" />
      </Deferred>
      <Deferred minHeight="900px" id="offer" data-id="element-16" data-mp="2svtr812lm86f">
        <OfferSection data-id="element-17" data-mp="2sv0pxfmesavo" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-ivory" id="faq" data-id="element-18" data-mp="2stzakyyd1285">
        <FaqSection data-id="element-19" data-mp="18c6qeaqww4i2" />
      </Deferred>
      <Deferred minHeight="420px" data-id="element-20" data-mp="1d4hor9607i8i">
        <FinalCta data-id="element-21" data-mp="1d5jb49sgzr5j" />
      </Deferred>
      <Deferred minHeight="220px" background="bg-charcoal-deep" data-id="element-22" data-mp="1d5suhej0f029">
        <Footer data-id="element-23" data-mp="ygmtbl0m2zrq" />
      </Deferred>

      <Suspense fallback={null} data-id="element-24" data-mp="ydiiw5wjz7sm">
        <StickyBuyBar data-id="element-25" data-mp="13eevd7uz029n" />
      </Suspense>
    </main>;
}